import React, { useRef, useState } from "react";
import { Alert, Snackbar } from "@mui/material";
import { Navigate, useNavigate } from "react-router-dom";
import UserDetailsHeader from "../../UserDetailsHeader";
import {
  HOW_TO_TEST_STS_SIDE,
  PERSON_DASHBOARD,
  USER_LIST,
} from "../../../../routes/routerPath";
import { getSearchParams } from "../../../../utlis/helpers";
import { useSection } from "../../../../i18n/useSection";
import RecordUploadChoice from "../../../modals/RecordUploadChoice";
import UploadVideoPreview from "../../../videoRecorder/UploadVideoPreview";
import CancelMeasurementButton from "../CancelMeasurementButton";
import MeasurementSessionStrip from "../MeasurementSessionStrip";
import InstructionText from "../InstructionText";
import HowToTestButton from "../HowToTestButton";
import {
  completeCurrentSessionModule,
  getSessionModuleRoute,
  prepareModuleForUpload,
  readMeasurementSession,
  getCurrentSessionModule,
  clearMeasurementSession,
} from "../measurementSession";
import { startMeasurementVideoUpload } from "../measurementBackgroundUpload";
import { notifyMeasurementUploadToast } from "../measurementUploadNotifications";
import { translate } from "../../../../utlis/locale";
import { getModuleName } from "../../../../utlis/measurementModuleLabels";
import { validateStsVideo } from "../videoUploadValidation";
import { STS_MODULE_ID } from "../../../../utlis/measurementModules";
import { getStsContext } from "./stsAccess";
import seatedIntro from "./sts-intro-seated.svg";
import "../tug/testModuleInfoPage.scss";
import "./sts.scss";

export default function StsSide() {
  const t = useSection("sts");
  const validationText = useSection("videoUpload");
  const navigate = useNavigate();
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const contextRef = useRef(getStsContext());
  const confirming = useRef(false);
  const [choice, setChoice] = useState(false);
  const [pending, setPending] = useState(null);
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
  const context = contextRef.current;
  const allowed = getStsContext();
  if (!context || !allowed || context.person.id !== allowed.person.id)
    return <Navigate to={`${USER_LIST}${getSearchParams()}`} replace />;

  const pick = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setChecking(true);
    const result = await validateStsVideo(file);
    setChecking(false);
    if (!result.valid) {
      setError(
        result.error === "orientation"
          ? t.validation_orientation
          : validationText[`validation_${result.error}`],
      );
      return;
    }
    setPending(file);
  };

  const confirm = (file) => {
    const current = getStsContext();
    if (
      confirming.current ||
      !file ||
      !current ||
      current.person.id !== context.person.id
    )
      return;
    confirming.current = true;
    const moduleInfo = { ...context.moduleInfo, moduleId: STS_MODULE_ID };
    sessionStorage.setItem("fromUpload", JSON.stringify(moduleInfo));
    notifyMeasurementUploadToast({
      severity: "info",
      message: translate("testModule", "upload_running", {
        module: getModuleName(moduleInfo),
      }),
    });
    startMeasurementVideoUpload({
      file,
      moduleInfo,
      userId: context.person.id,
    });
    setPending(null);
    const session = readMeasurementSession();
    const currentModule = getCurrentSessionModule(session);
    const canAdvance =
      Number(currentModule?.moduleId) === STS_MODULE_ID &&
      String(session?.personId) === String(context.person.id);
    const { next, isComplete } = canAdvance
      ? completeCurrentSessionModule()
      : { isComplete: true };
    if (!canAdvance) clearMeasurementSession();
    const nextRoute =
      !isComplete && next && getSessionModuleRoute(next.moduleId);
    if (nextRoute) {
      prepareModuleForUpload(next);
      navigate(nextRoute + getSearchParams());
      return;
    }
    const params = new URLSearchParams(getSearchParams());
    params.set("tab", "metrics");
    params.set("seniorId", context.person.id);
    params.set("seniorUuid", context.person.id);
    navigate(`${PERSON_DASHBOARD}?${params.toString()}`);
  };

  if (pending) {
    return (
      <UserDetailsHeader
        className="measurement-shell"
        onBack={() => setPending(null)}
      >
        <UploadVideoPreview
          file={pending}
          onConfirm={confirm}
          onCancel={() => setPending(null)}
          title={t.selected}
          moduleTitle={t.title}
          note={t.selected_note}
          confirmLabel={t.upload_now}
          variant="sts"
        />
      </UserDetailsHeader>
    );
  }

  return (
    <>
      <RecordUploadChoice
        variant="sts"
        open={choice}
        onClose={() => setChoice(false)}
        onRecord={() => {
          setChoice(false);
          cameraRef.current?.click();
        }}
        onUpload={() => {
          setChoice(false);
          galleryRef.current?.click();
        }}
      />
      <UserDetailsHeader className="measurement-shell">
        <main
          className="sts-page measurement-intro tug-test-info-wrapper"
          aria-busy={checking}
        >
          <MeasurementSessionStrip moduleId={STS_MODULE_ID} />
          <h1>{t.title}</h1>
          <img
            className="sts-intro-figure"
            src={seatedIntro}
            alt={t.illustration_seated}
          />
          <p>{t.description}</p>
          <HowToTestButton
            label={t.how_to_test}
            onClick={() => navigate(HOW_TO_TEST_STS_SIDE + getSearchParams())}
          />
          <p className="sts-important">
            <InstructionText text={t.important} label />
          </p>
          <input
            hidden
            aria-label={t.record_upload}
            data-testid="sts-camera"
            type="file"
            accept="video/*"
            capture="environment"
            ref={cameraRef}
            onChange={pick}
          />
          <input
            hidden
            aria-label={t.upload}
            data-testid="sts-gallery"
            type="file"
            accept=".mp4,.mov,video/mp4,video/quicktime"
            ref={galleryRef}
            onChange={pick}
          />
          <button
            className="sts-primary"
            disabled={checking}
            onClick={() => setChoice(true)}
          >
            {t.record_upload}
          </button>
          <CancelMeasurementButton />
        </main>
      </UserDetailsHeader>
      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError("")}
      >
        <Alert
          severity="error"
          closeText={t.close}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}
